export interface RenderFeatureFlags {
  selectiveBloom: boolean;
  cinematicPost: boolean;
  worldScatter: boolean;
  engineTrails: boolean;
  pooledVfx: boolean;
  blobShadows: boolean;
  dynamicCamera: boolean;
  terrainContours: boolean;
  depthOfField: boolean;
}

export interface VisualQualityPreset {
  id: 'performance' | 'balanced' | 'ultra';
  devicePixelRatioCap: number;
  bloomStrength: number;
  bloomThreshold: number;
  bloomRadius: number;
  postIntensity: number;
  scatterDensity: number;
  trailBudget: number;
  vfxParticleScale: number;
  features: RenderFeatureFlags;
  /**
   * When false, GameClient skips the local-player-kill hit-stop and the
   * heavy-hit screen shake. Defaults to true on every preset; an end-user
   * "reduce motion" toggle can flip it without touching the rest of the
   * preset. Independent of `features.dynamicCamera`, which controls the
   * pre-existing per-event camera shake on CameraController.
   */
  screenShake: boolean;
}

export const VISUAL_QUALITY_PRESETS: Record<VisualQualityPreset['id'], VisualQualityPreset> = {
  performance: {
    id: 'performance',
    devicePixelRatioCap: 1.25,
    bloomStrength: 0.55,
    bloomThreshold: 0.76,
    bloomRadius: 0.7,
    postIntensity: 0.45,
    scatterDensity: 0.55,
    trailBudget: 28,
    vfxParticleScale: 0.75,
    screenShake: true,
    features: {
      selectiveBloom: true,
      cinematicPost: true,
      worldScatter: true,
      engineTrails: true,
      pooledVfx: true,
      blobShadows: true,
      dynamicCamera: true,
      terrainContours: false,
      depthOfField: false,
    },
  },
  balanced: {
    id: 'balanced',
    devicePixelRatioCap: 1.6,
    bloomStrength: 0.78,
    bloomThreshold: 0.70,
    bloomRadius: 0.82,
    postIntensity: 0.9,
    scatterDensity: 1,
    trailBudget: 52,
    vfxParticleScale: 1,
    screenShake: true,
    features: {
      selectiveBloom: true,
      cinematicPost: true,
      worldScatter: true,
      engineTrails: true,
      pooledVfx: true,
      blobShadows: true,
      dynamicCamera: true,
      terrainContours: true,
      depthOfField: true,
    },
  },
  ultra: {
    id: 'ultra',
    devicePixelRatioCap: 2,
    bloomStrength: 0.95,
    bloomThreshold: 0.66,
    bloomRadius: 0.92,
    postIntensity: 1,
    scatterDensity: 1.35,
    trailBudget: 72,
    vfxParticleScale: 1.2,
    screenShake: true,
    features: {
      selectiveBloom: true,
      cinematicPost: true,
      worldScatter: true,
      engineTrails: true,
      pooledVfx: true,
      blobShadows: true,
      dynamicCamera: true,
      terrainContours: true,
      depthOfField: true,
    },
  },
};

export const DEFAULT_VISUAL_QUALITY_PRESET = VISUAL_QUALITY_PRESETS.performance;
